import {
  computed,
  type ComputedRef,
  type MaybeRefOrGetter,
  toValue
} from "vue";

import {
  ResourceAPIResponse,
  UNASSIGNED_RESOURCE_ID
} from "@/typings/scheduler";

/** The chip that applies no department filter. */
export const ALL_DEPARTMENTS = "all";

export const NO_DEPARTMENT_LABEL = "No department";

// Distinct from the label so renaming the heading cannot silently repartition
// the groups.
const NO_DEPARTMENT_KEY = "__no_department__";

/** A technician row, or the Unassigned pseudo-row that stands in for one. */
export type SelectableResource = Pick<ResourceAPIResponse, "id" | "full_name"> &
  Partial<ResourceAPIResponse>;

export interface DepartmentOption {
  id: number;
  name: string;
}

export interface TechnicianGroup {
  key: string;
  label: string;
  technicians: SelectableResource[];
}

interface TechnicianSelectionInput {
  resources: MaybeRefOrGetter<SelectableResource[]>;
  departments: MaybeRefOrGetter<DepartmentOption[]>;
  query: MaybeRefOrGetter<string>;
  departmentId: MaybeRefOrGetter<number | typeof ALL_DEPARTMENTS>;
}

export interface TechnicianSelection {
  unassignedRow: ComputedRef<SelectableResource | null>;
  groups: ComputedRef<TechnicianGroup[]>;
  matches: ComputedRef<SelectableResource[]>;
  isSearching: ComputedRef<boolean>;
  isEmpty: ComputedRef<boolean>;
  listedIds: ComputedRef<number[]>;
}

// `useEventCalendar`'s Unassigned pseudo-row is not a technician: it carries
// only `id`, `full_name`, an empty avatar and an empty department, so every
// field this module reads off a technician has to be optional-safe.
const isUnassigned = (resource: SelectableResource) =>
  resource.id === UNASSIGNED_RESOURCE_ID;

const matchesQuery = (resource: SelectableResource, query: string) => {
  const haystack = [resource.full_name, resource.department?.name]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(query);
};

// The Unassigned row has no `belongs_to_departments`, which is why every caller
// filters it out before reaching here rather than relying on optional chaining
// to return a falsey answer.
const belongsToDepartment = (resource: SelectableResource, id: number) =>
  Boolean(resource.belongs_to_departments?.includes(id));

/**
 * Derives what the technician sheet shows from the roster, the departments, the
 * search term and the active department chip — and the set of ids a "Select
 * all" should toggle, which is always *exactly what is currently listed*.
 *
 * Grouping keys off a technician's primary `department`, while the chip filters
 * on `belongs_to_departments`, mirroring the board filter in `useEventCalendar`.
 * A technician in several departments therefore appears under one heading but
 * matches several chips.
 */
export function useTechnicianSelection({
  resources,
  departments,
  query,
  departmentId
}: TechnicianSelectionInput): TechnicianSelection {
  const normalizedQuery = computed(() => toValue(query).trim().toLowerCase());
  const isSearching = computed(() => normalizedQuery.value.length > 0);
  const activeDepartment = computed(() => toValue(departmentId));

  /**
   * Exempt from the department chips. The pseudo-row belongs to no department,
   * so a chip has nothing to say about it either way — and on the phone board
   * Unassigned is a column rather than a modal list, so a chip that hid it
   * would take the pool a dispatcher is filtering in order to assign FROM off
   * the screen at exactly the moment they want it.
   *
   * Still subject to the search term, which is a name match rather than a
   * filter: typing a technician's name is asking for that person.
   */
  const unassignedRow = computed<SelectableResource | null>(() => {
    const row = toValue(resources).find(isUnassigned);
    if (!row) return null;
    if (isSearching.value && !matchesQuery(row, normalizedQuery.value)) {
      return null;
    }
    return row;
  });

  /** Technicians left after the chip and the search term, order preserved. */
  const matches = computed(() =>
    toValue(resources)
      .filter((resource) => !isUnassigned(resource))
      .filter((technician) => {
        if (
          activeDepartment.value !== ALL_DEPARTMENTS &&
          !belongsToDepartment(technician, activeDepartment.value)
        ) {
          return false;
        }
        return (
          !isSearching.value || matchesQuery(technician, normalizedQuery.value)
        );
      })
  );

  /**
   * Empty while searching: the story hides section headings so the matches read
   * as one flat list, and an empty array is how the component is told to render
   * `matches` instead.
   */
  const groups = computed<TechnicianGroup[]>(() => {
    if (isSearching.value) return [];

    const byDepartment = new Map<string, TechnicianGroup>();

    matches.value.forEach((technician) => {
      const department = technician.department;
      const key = department?.id ? String(department.id) : NO_DEPARTMENT_KEY;

      if (!byDepartment.has(key)) {
        byDepartment.set(key, {
          key,
          label: department?.name || NO_DEPARTMENT_LABEL,
          technicians: []
        });
      }
      byDepartment.get(key)?.technicians.push(technician);
    });

    // Ordered by the departments list so headings are stable rather than
    // following whatever order the roster happens to arrive in. Departments the
    // store has not supplied still get a heading, appended after the known ones
    // — otherwise an empty or stale departments list silently hides technicians.
    const known = toValue(departments).map((department) =>
      String(department.id)
    );
    const ordering = [
      ...known,
      ...[...byDepartment.keys()].filter(
        (key) => key !== NO_DEPARTMENT_KEY && !known.includes(key)
      ),
      NO_DEPARTMENT_KEY
    ];

    return ordering
      .map((key) => byDepartment.get(key))
      .filter((group): group is TechnicianGroup => Boolean(group));
  });

  const isEmpty = computed(
    () => !unassignedRow.value && matches.value.length === 0
  );

  /**
   * The "Select all" scope. Deliberately *what is listed* rather than the whole
   * roster: with a chip or a search term active, Select all must not reach rows
   * the admin cannot currently see. Unassigned is listed under a chip, so it is
   * in scope there; a search term that does not match its name still hides it,
   * and Select all then leaves its selected state alone.
   */
  const listedIds = computed(() => {
    const ids = matches.value.map((technician) => technician.id);
    return unassignedRow.value ? [unassignedRow.value.id, ...ids] : ids;
  });

  return {
    unassignedRow,
    groups,
    matches,
    isSearching,
    isEmpty,
    listedIds
  };
}
